"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.newAttributeLocator = exports.newMeasureSort = exports.newAttributeAreaSort = exports.newAttributeSort = exports.measureLocatorIdentifier = exports.attributeLocatorElement = exports.attributeLocatorIdentifier = exports.sortMeasureLocators = exports.sortEntityIds = exports.sortDirection = exports.isMeasureLocator = exports.isAttributeLocator = exports.isMeasureSort = exports.isAttributeAreaSort = exports.isAttributeSort = void 0;
var tslib_1 = require("tslib");
var attribute_1 = require("../attribute");
var measure_1 = require("../measure");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
//
// Type guards
//
/**
 * Type guard checking whether an object is an attribute sort item.
 *
 * @public
 */
function isAttributeSort(obj) {
    return !isEmpty_1.default(obj) && obj.attributeSortItem !== undefined;
}
exports.isAttributeSort = isAttributeSort;
/**
 * Type guard checking whether an object is an attribute area sort item.
 *
 * @public
 */
function isAttributeAreaSort(obj) {
    return isAttributeSort(obj) && obj.attributeSortItem.aggregation !== undefined;
}
exports.isAttributeAreaSort = isAttributeAreaSort;
/**
 * Type guard checking whether an object is a measure sort item.
 *
 * @public
 */
function isMeasureSort(obj) {
    return !isEmpty_1.default(obj) && obj.measureSortItem !== undefined;
}
exports.isMeasureSort = isMeasureSort;
/**
 * Type guard checking whether an object is an attribute locator.
 *
 * @public
 */
function isAttributeLocator(obj) {
    return !isEmpty_1.default(obj) && obj.attributeLocatorItem !== undefined;
}
exports.isAttributeLocator = isAttributeLocator;
/**
 * Type guard checking whether an object is measure locator
 *
 * @public
 */
function isMeasureLocator(obj) {
    return !isEmpty_1.default(obj) && obj.measureLocatorItem !== undefined;
}
exports.isMeasureLocator = isMeasureLocator;
//
// Public functions
//
/**
 * Gets sort item's direction
 * @param sort - sort item.
 * @public
 */
function sortDirection(sort) {
    ts_invariant_1.default(sort, "sort item must be specified");
    if (isAttributeSort(sort)) {
        return sort.attributeSortItem.direction;
    }
    else {
        return sort.measureSortItem.direction;
    }
}
exports.sortDirection = sortDirection;
/**
 * Given sort item, returns ids of entities (objects) that are referenced by the sort item.
 *
 * The ids are returned in an categorized way.
 *
 * @public
 */
function sortEntityIds(sort) {
    ts_invariant_1.default(sort, "sort item must be specified");
    var res = {
        attributeIdentifiers: [],
        measureIdentifiers: [],
        allIdentifiers: [],
    };
    if (isAttributeSort(sort)) {
        var attrId = sort.attributeSortItem.attributeIdentifier;
        res.attributeIdentifiers.push(attrId);
        res.allIdentifiers.push(attrId);
    }
    else if (isMeasureSort(sort)) {
        sort.measureSortItem.locators.forEach(function (loc) {
            if (isAttributeLocator(loc)) {
                var attrId = loc.attributeLocatorItem.attributeIdentifier;
                res.attributeIdentifiers.push(attrId);
                res.allIdentifiers.push(attrId);
            }
            else if (isMeasureLocator(loc)) {
                var measureId = loc.measureLocatorItem.measureIdentifier;
                res.measureIdentifiers.push(measureId);
                res.allIdentifiers.push(measureId);
            }
        });
    }
    return res;
}
exports.sortEntityIds = sortEntityIds;
/**
 * Given a measure sort item, return the locators which identify the measure (possibly scoped for particular
 * attribute element).
 *
 * @param sort - measure sort items
 * @returns measure sort locators
 * @public
 */
function sortMeasureLocators(sort) {
    return sort.measureSortItem.locators;
}
exports.sortMeasureLocators = sortMeasureLocators;
/**
 * Given attribute locator, return the localId of attribute that it references.
 *
 * @param locator - attribute locator
 * @returns attribute localId
 * @public
 */
function attributeLocatorIdentifier(locator) {
    return locator.attributeLocatorItem.attributeIdentifier;
}
exports.attributeLocatorIdentifier = attributeLocatorIdentifier;
/**
 * Given attribute locator, return the element that it references.
 *
 * @param locator - attribute locator
 * @returns attribute element
 * @public
 */
function attributeLocatorElement(locator) {
    return locator.attributeLocatorItem.element;
}
exports.attributeLocatorElement = attributeLocatorElement;
/**
 * Given measure locator, return the localId of measure that it references.
 *
 * @param locator - measure locator
 * @returns measure localId
 * @public
 */
function measureLocatorIdentifier(locator) {
    return locator.measureLocatorItem.measureIdentifier;
}
exports.measureLocatorIdentifier = measureLocatorIdentifier;
/**
 * Creates a new attribute sort - sorting the result by values of the provided attribute's elements. The attribute
 * can be either specified by value or by reference using its local identifier.
 *
 * @param attributeOrId - attribute to sort by
 * @param sortDirection - asc or desc, defaults to "asc"
 * @returns always new item
 * @public
 */
function newAttributeSort(attributeOrId, sortDirection) {
    if (sortDirection === void 0) { sortDirection = "asc"; }
    ts_invariant_1.default(attributeOrId, "attribute to create sort for must be specified");
    var id = attribute_1.attributeLocalId(attributeOrId);
    return {
        attributeSortItem: {
            attributeIdentifier: id,
            direction: sortDirection,
        },
    };
}
exports.newAttributeSort = newAttributeSort;
/**
 * Creates a new attribute area sort - sorting the result by aggregated measure values belonging to each
 * attribute value included in the result.
 *
 * @param attributeOrId - attribute to sort by
 * @param sortDirection - sorting direction
 * @param aggregation - area sort aggregation function. only "sum" is supported at the moment.
 * @public
 */
function newAttributeAreaSort(attributeOrId, sortDirection, aggregation) {
    if (sortDirection === void 0) { sortDirection = "asc"; }
    if (aggregation === void 0) { aggregation = "sum"; }
    ts_invariant_1.default(attributeOrId, "attribute to create sort for must be specified");
    var id = attribute_1.attributeLocalId(attributeOrId);
    return {
        attributeSortItem: {
            attributeIdentifier: id,
            direction: sortDirection,
            aggregation: aggregation,
        },
    };
}
exports.newAttributeAreaSort = newAttributeAreaSort;
/**
 * Creates a new measure sort - sorting the result by values of the provided measure. The measure
 * can be either specified by value or by reference using its local identifier.
 *
 * @param measureOrId - measure to sort by
 * @param sortDirection - asc or desc, defaults to "asc"
 * @param attributeLocators - optional attribute locators
 * @returns new sort item
 * @public
 */
function newMeasureSort(measureOrId, sortDirection, attributeLocators) {
    if (sortDirection === void 0) { sortDirection = "asc"; }
    if (attributeLocators === void 0) { attributeLocators = []; }
    ts_invariant_1.default(measureOrId, "measure to create sort for must be specified");
    var id = measure_1.measureLocalId(measureOrId);
    return {
        measureSortItem: {
            direction: sortDirection,
            locators: tslib_1.__spreadArrays(attributeLocators, [
                {
                    measureLocatorItem: {
                        measureIdentifier: id,
                    },
                },
            ]),
        },
    };
}
exports.newMeasureSort = newMeasureSort;
/**
 * Creates a new attribute locator for an attribute element.
 *
 * @param attributeOrId - attribute, can be specified by either the attribute object or its local identifier
 * @param element - attribute element value URI or primary label value
 * @returns new locator
 * @public
 */
function newAttributeLocator(attributeOrId, element) {
    ts_invariant_1.default(attributeOrId, "attribute to create sort locator for must be specified");
    ts_invariant_1.default(element, "attribute element must be specified");
    var localId = attribute_1.attributeLocalId(attributeOrId);
    return {
        attributeLocatorItem: {
            attributeIdentifier: localId,
            element: element,
        },
    };
}
exports.newAttributeLocator = newAttributeLocator;
//# sourceMappingURL=sort.js.map